/** @typedef {import('../routeSchema').RouteNode} RouteNode */
import {
  modulePermission,
  PERMISSION_MODULES,
  FEATURE_PERMISSIONS,
  NAV_ROUTE_PERMISSIONS,
} from "../../permissions";

const view = (module) => modulePermission.view(module);
const create = (module) => modulePermission.create(module);
const edit = (module) => modulePermission.edit(module);

/** @type {RouteNode[]} */
const adminSubheaderRoutes = [
  {
    type: "route",
    id: "super-members",
    name: "Super Members",
    path: "/super-members",
    component: "SuperMembers",
  },
  {
    type: "route",
    id: "super-members-add",
    name: "Add Super Member",
    path: "/super-members/add",
    component: "AddSuperMember",
  },
  {
    type: "route",
    id: "my-profile-admin",
    name: "Admin Profile",
    path: "/my-profile-admin",
    component: "Profile",
  },
  {
    type: "route",
    id: "help-admin",
    name: "Admin Help",
    path: "/help-admin",
    component: "HelpPage",
  },
  {
    type: "route",
    id: "bg-verify",
    name: "Background Verification",
    path: "/bgVerify",
    component: "BgVerify",
  },
];

/** @type {RouteNode[]} */
const mainAppRoutes = [
  {
    type: "route",
    id: "mobile-inventory",
    name: "Mobile Inventory",
    path: "/mobile-inventory",
    component: "MobileInventory",
    permission: view(PERMISSION_MODULES.INVENTORY),
  },
  {
    type: "route",
    id: "mobile-inventory-add",
    name: "Add Inventory",
    path: "/mobile-inventory/add",
    component: "MobileInventoryForm",
    permission: create(PERMISSION_MODULES.INVENTORY),
  },
  {
    type: "route",
    id: "mobile-inventory-edit",
    name: "Edit Inventory",
    path: "/mobile-inventory/edit/:id",
    component: "MobileInventoryForm",
    permission: edit(PERMISSION_MODULES.INVENTORY),
  },
  {
    type: "route",
    id: "mobile-inventory-transfer-create",
    name: "Transfer Inventory",
    path: "/mobile-inventory/transfer",
    component: "MobileInventoryTransfer",
    permission: edit(PERMISSION_MODULES.INVENTORY),
  },
  {
    type: "route",
    id: "mobile-inventory-transfer",
    name: "Transfer Inventory",
    path: "/mobile-inventory/transfer/:id",
    component: "MobileInventoryTransfer",
    permission: edit(PERMISSION_MODULES.INVENTORY),
  },
  {
    type: "route",
    id: "mobile-inventory-disposal-create",
    name: "Dispose Inventory",
    path: "/mobile-inventory/dispose",
    component: "MobileInventoryDisposal",
    permission: edit(PERMISSION_MODULES.INVENTORY),
  },
  {
    type: "route",
    id: "mobile-inventory-disposal",
    name: "Dispose Inventory",
    path: "/mobile-inventory/dispose/:id",
    component: "MobileInventoryDisposal",
    permission: edit(PERMISSION_MODULES.INVENTORY),
  },
  {
    type: "route",
    id: "mobile-inventory-orders-list",
    name: "Purchase Orders",
    path: "/mobile-inventory/orders",
    component: "MobileInventoryOrdersList",
    permission: view(PERMISSION_MODULES.INVENTORY),
  },
  {
    type: "route",
    id: "mobile-inventory-orders-create",
    name: "Create Inventory Order",
    path: "/mobile-inventory/orders/create",
    component: "MobileInventoryOrdersForm",
    permission: edit(PERMISSION_MODULES.INVENTORY),
  },
  {
    type: "route",
    id: "mobile-inventory-orders",
    name: "Update Inventory Order",
    path: "/mobile-inventory/orders/:orderId",
    component: "MobileInventoryOrdersForm",
    permission: edit(PERMISSION_MODULES.INVENTORY),
  },
  {
    type: "route",
    id: "mobile-maintenance",
    name: "Mobile Maintenance",
    path: "/mobile-maintenance",
    component: "MobileMaintenance",
    permission: view(PERMISSION_MODULES.INVENTORY),
  },
  {
    type: "route",
    id: "inventory-counts",
    name: "Physical Count",
    path: "/inventory-counts",
    component: "InventoryCountsPage",
    permission: view(PERMISSION_MODULES.INVENTORY),
  },
  {
    type: "route",
    id: "inventory-count-detail",
    name: "Physical Count Detail",
    path: "/inventory-counts/:countId",
    component: "InventoryCountDetailPage",
    permission: view(PERMISSION_MODULES.INVENTORY),
  },
  {
    type: "route",
    id: "notification-group",
    name: "Notification Group",
    path: "/notification-group",
    component: "NotificationGroup",
    permission: view(PERMISSION_MODULES.SETTINGS),
  },
  {
    type: "route",
    id: "inventory-items",
    name: "Items",
    path: "/inventory-items",
    component: "InventoryCatalog",
    permission: view(PERMISSION_MODULES.SETTINGS),
  },
  {
    type: "route",
    id: "inventory-items-add",
    name: "Add Item",
    path: "/inventory-items/add",
    component: "InventoryCatalogForm",
    permission: create(PERMISSION_MODULES.SETTINGS),
  },
  {
    type: "route",
    id: "inventory-items-edit",
    name: "Edit Item",
    path: "/inventory-items/:id/edit",
    component: "InventoryCatalogForm",
    permission: edit(PERMISSION_MODULES.SETTINGS),
  },
  {
    type: "route",
    id: "notification-group-add",
    name: "Add Notification Group",
    path: "/notification-group/add",
    component: "NotificationGroupForm",
    permission: create(PERMISSION_MODULES.SETTINGS),
  },
  {
    type: "route",
    id: "notification-group-edit",
    name: "Edit Notification Group",
    path: "/notification-group/:id/edit",
    component: "NotificationGroupForm",
    permission: edit(PERMISSION_MODULES.SETTINGS),
  },
  {
    type: "route",
    id: "notification-group-view",
    name: "View Notification Group",
    path: "/notification-group/view/:id",
    component: "NotificationGroupView",
    permission: view(PERMISSION_MODULES.SETTINGS),
  },
  {
    type: "route",
    id: "defects",
    name: "Defects",
    path: "/defects",
    component: "DefectManagement",
    permission: NAV_ROUTE_PERMISSIONS.DEFECTS,
  },
  {
    type: "route",
    id: "defect-create",
    name: "Create Manual Defect",
    path: "/defects/create",
    component: "DefectCreate",
    permission: NAV_ROUTE_PERMISSIONS.DEFECTS_CREATE,
  },
  {
    type: "route",
    id: "defect-logs",
    name: "Defect Logs",
    path: "/defects/view/:id/logs",
    component: "DefectLogsPage",
    permission: NAV_ROUTE_PERMISSIONS.DEFECTS,
  },
  {
    type: "route",
    id: "defect-view",
    name: "View Defect",
    path: "/defects/view/:id",
    component: "DefectView",
    permission: NAV_ROUTE_PERMISSIONS.DEFECTS,
  },
  {
    type: "route",
    id: "work-orders",
    name: "Work Orders",
    path: "/work-orders",
    component: "WorkOrderManagement",
    permission: NAV_ROUTE_PERMISSIONS.WORK_ORDERS,
  },
  {
    type: "route",
    id: "work-order-create",
    name: "Create Work Order",
    path: "/work-orders/create",
    component: "WorkOrderCreate",
    permission: NAV_ROUTE_PERMISSIONS.WORK_ORDERS_CREATE,
  },
  {
    type: "route",
    id: "work-order-edit",
    name: "Edit Work Order",
    path: "/work-orders/edit/:id",
    component: "WorkOrderCreate",
    permission: NAV_ROUTE_PERMISSIONS.WORK_ORDERS_EDIT,
  },
  {
    type: "route",
    id: "work-order-view",
    name: "View Work Order",
    path: "/work-orders/view/:id",
    component: "WorkOrderView",
    permission: NAV_ROUTE_PERMISSIONS.WORK_ORDERS,
  },
  {
    type: "route",
    id: "dashboard",
    name: "Dashboard",
    path: "/dashboard",
    component: "Dashboard",
    permission: FEATURE_PERMISSIONS.DASHBOARD_REPORTS,
  },
  {
    type: "route",
    id: "mentee-doc-upload",
    name: "Mentee Document Upload",
    path: "/mentee-doc-upload/:id",
    component: "DocumentUpload",
  },
  {
    type: "route",
    id: "bulk-upload",
    name: "Bulk Upload",
    path: "/bulk-upload",
    component: "BulkUpload",
    permission: FEATURE_PERMISSIONS.BULK_UPLOAD,
  },
  {
    type: "route",
    id: "permission-management",
    name: "Permission Management",
    path: "/permission-management",
    component: "PermissionManagement",
    permission: FEATURE_PERMISSIONS.MANAGE_PERMISSIONS,
  },
  {
    type: "route",
    id: "audit-logs",
    name: "Audit Logs",
    path: "/audit-logs",
    component: "AuditLogs",
    permission: view(PERMISSION_MODULES.SETTINGS),
  },
  {
    type: "route",
    id: "nomination-approvals",
    name: "Nomination Approvals",
    path: "/nomination-approvals",
    component: "NominationApprovals",
    permission: view(PERMISSION_MODULES.SETTINGS),
  },
  {
    type: "route",
    id: "equipment-form-mapping",
    name: "Equipment Form Mapping",
    path: "/equipment-form-mapping",
    component: "EquipmentFormMapping",
    permission: edit(PERMISSION_MODULES.SETTINGS),
  },
  {
    type: "route",
    id: "donation",
    name: "Donation",
    path: "/donation",
    component: "Donation",
    active: false,
  },
  {
    type: "route",
    id: "global-search",
    name: "Search",
    path: "/search",
    component: "GlobalSearch",
  },
  {
    type: "route",
    id: "programs",
    name: "Programs",
    path: "/programs",
    component: "Programs",
    permission: view(PERMISSION_MODULES.EVENTS),
  },
  {
    type: "route",
    id: "program-details",
    name: "Program Details",
    path: "/program-details/:id",
    component: "ProgramDetails",
    permission: view(PERMISSION_MODULES.EVENTS),
  },
  {
    type: "route",
    id: "patient-registration-view",
    name: "Patient Registration Details",
    path: "/program-details/:programId/patient-registration/:registrationId",
    component: "ViewPatientDetails",
    permission: view(PERMISSION_MODULES.PATIENTS),
  },
  {
    type: "route",
    id: "assign-task",
    name: "Assign Task",
    path: "/assign-task/:id",
    component: "AssignTask",
    permission: edit(PERMISSION_MODULES.EVENTS),
  },
  {
    type: "route",
    id: "assign-mentees-by-id",
    name: "Assign Mentees",
    path: "/assign-mentees/:id",
    component: "AssignMentees",
    permission: FEATURE_PERMISSIONS.STAFFING_EDIT,
  },
  {
    type: "route",
    id: "assign-mentees",
    name: "Assign Mentees",
    path: "/assign-mentees",
    component: "AssignMentees",
    permission: FEATURE_PERMISSIONS.STAFFING_EDIT,
  },
  {
    type: "route",
    id: "start-program",
    name: "Start Program",
    path: "/start-program/:id",
    component: "AssignTask",
    permission: edit(PERMISSION_MODULES.EVENTS),
  },
  {
    type: "route",
    id: "program-completion",
    name: "Program Completion",
    path: "/program-completion/:id",
    component: "ProgramCompletion",
    permission: view(PERMISSION_MODULES.EVENTS),
  },
  {
    type: "route",
    id: "create-programs",
    name: "Create Program",
    path: "/create-programs",
    component: "CreatePrograms",
    permission: create(PERMISSION_MODULES.EVENTS),
  },
  {
    type: "route",
    id: "update-program",
    name: "Update Program",
    path: "/update-program/:id",
    component: "CreatePrograms",
    permission: edit(PERMISSION_MODULES.EVENTS),
  },
  {
    type: "route",
    id: "calendar",
    name: "Calendar",
    path: "/calendar",
    component: "Scheduler",
    permission: view(PERMISSION_MODULES.CALENDAR),
  },
  {
    type: "route",
    id: "members",
    name: "Members",
    path: "/members",
    component: "Members",
    permission: FEATURE_PERMISSIONS.MEMBERS_LIST,
  },
  {
    type: "route",
    id: "members-add",
    name: "Create Member",
    path: "/members/add",
    component: "CreateNewUser",
    permission: FEATURE_PERMISSIONS.ADD_USER,
  },
  {
    type: "route",
    id: "equipment",
    name: "Mobile Units",
    path: "/equipment",
    component: "MobileUnits",
    permission: view(PERMISSION_MODULES.MOBILE_UNITS),
  },
  {
    type: "route",
    id: "mobileunit-add",
    name: "Add Mobile Unit",
    path: "/mobileunit/add",
    component: "AddMobileUnit",
    permission: create(PERMISSION_MODULES.MOBILE_UNITS),
  },
  {
    type: "route",
    id: "mobileunit-update",
    name: "Update Mobile Unit",
    path: "/mobileunit/update/:id",
    component: "UpdateMobileUnit",
    permission: FEATURE_PERMISSIONS.EDIT_FLEET,
  },
  {
    type: "route",
    id: "equipment-view",
    name: "Equipment View",
    path: "/equipment/:id",
    component: "EquipmentView",
    permission: view(PERMISSION_MODULES.MOBILE_UNITS),
  },
  {
    type: "route",
    id: "equipment-history",
    name: "Equipment History",
    path: "/equipmentHistory",
    component: "EquipmentHistory",
    permission: view(PERMISSION_MODULES.MOBILE_UNITS),
  },
  {
    type: "route",
    id: "discussions",
    name: "Discussions",
    path: "/discussions",
    component: "DiscussionDetails",
    permission: view(PERMISSION_MODULES.MESSAGES),
  },
  {
    type: "route",
    id: "mentor-profile",
    name: "Mentor Profile",
    path: "/mentor-profile/:id",
    component: "ProfileView",
  },
  {
    type: "route",
    id: "help",
    name: "Help",
    path: "/help",
    component: "HelpPage",
  },
  {
    type: "route",
    id: "notification",
    name: "Notifications",
    path: "/notification",
    component: "NotificationMenu",
    permission: view(PERMISSION_MODULES.ACCOUNT),
  },
  {
    type: "route",
    id: "my-profile",
    name: "My Profile",
    path: "/my-profile",
    component: "Profile",
    permission: view(PERMISSION_MODULES.ACCOUNT),
  },
  {
    type: "route",
    id: "profile",
    name: "Profile",
    path: "/profile",
    component: "MyProfile",
    permission: view(PERMISSION_MODULES.ACCOUNT),
  },
  {
    type: "route",
    id: "profile-by-id",
    name: "Profile By Id",
    path: "/profile/:id",
    component: "MyProfile",
    permission: view(PERMISSION_MODULES.ACCOUNT),
  },
  {
    type: "route",
    id: "deployments",
    name: "Deployments",
    path: "/deployments/:id",
    component: "DeploymentComponent",
  },

  {
    type: "route",
    id: "mentor-details",
    name: "Mentor Details",
    path: "/mentor-details/:id",
    component: "ProfileView",
  },
  {
    type: "route",
    id: "mentor-tasks",
    name: "Mentor Tasks",
    path: "/mentor-tasks",
    component: "MentorTask",
  },
  {
    type: "route",
    id: "mentor-tasks-details",
    name: "Mentor Task Details",
    path: "/mentor-tasks-details/:id",
    component: "MentorTaskDetails",
  },
  {
    type: "route",
    id: "mentees",
    name: "Mentees",
    path: "/mentees",
    component: "Mentees",
  },
  {
    type: "route",
    id: "mentee-details",
    name: "Mentee Details",
    path: "/mentee-details/:id",
    component: "ProfileView",
  },
  {
    type: "route",
    id: "all-request",
    name: "All Requests",
    path: "/all-request",
    component: "AllRequest",
  },
  {
    type: "route",
    id: "reports",
    name: "Reports",
    path: "/reports",
    component: "Reports",
    permission: view(PERMISSION_MODULES.REPORTS),
  },
  {
    type: "route",
    id: "create-report",
    name: "Create Report",
    path: "/create-report",
    component: "CreateReport",
    permission: create(PERMISSION_MODULES.REPORTS),
  },
  {
    type: "route",
    id: "edit-report",
    name: "Edit Report",
    path: "/edit-report/:id",
    component: "EditReport",
    permission: edit(PERMISSION_MODULES.REPORTS),
  },
  {
    type: "route",
    id: "view-report",
    name: "View Report",
    path: "/view-report/:id",
    component: "ViewReport",
    permission: view(PERMISSION_MODULES.REPORTS),
  },
  {
    type: "route",
    id: "profile-view",
    name: "Mentor Mentee Profile",
    path: "/profileView",
    component: "MentorMenteeProfile",
  },
  {
    type: "route",
    id: "category",
    name: "Category",
    path: "/category",
    component: "Category",
    permission: FEATURE_PERMISSIONS.CATEGORIES,
  },
  {
    type: "route",
    id: "category-view",
    name: "Category View",
    path: "/categoryView",
    component: "CategoryView",
    permission: FEATURE_PERMISSIONS.CATEGORIES,
  },
  {
    type: "route",
    id: "view-task",
    name: "View Task",
    path: "/viewTask/:id",
    component: "ViewTask",
  },
  {
    type: "route",
    id: "testimonial-view",
    name: "Testimonial View",
    path: "/testimonialView/:id",
    component: "TestimonialView",
  },
  {
    type: "route",
    id: "history-notes",
    name: "History Notes",
    path: "/historyNotes/:id",
    component: "HistoryNotes",
  },
  {
    type: "route",
    id: "timesheet",
    name: "Time Sheet",
    path: "/timeSheet",
    component: "TimeSheet",
    permission: view(PERMISSION_MODULES.TIMESHEET),
  },
  {
    type: "route",
    id: "mentee-timesheet",
    name: "Mentee Time Sheet",
    path: "/menteeTimeSheet",
    component: "TimeSheet",
    permission: view(PERMISSION_MODULES.TIMESHEET),
  },
  {
    type: "route",
    id: "mentor-program-timesheet",
    name: "Mentor Program Time Sheet",
    path: "/mentorProgramTimeSheet",
    component: "MentorProgramTimeSheet",
    permission: view(PERMISSION_MODULES.TIMESHEET),
  },
  {
    type: "route",
    id: "admin-timesheet",
    name: "Admin Time Sheet",
    path: "/adminTimeSheet",
    component: "AdminTimeSheet",
    permission: view(PERMISSION_MODULES.TIMESHEET),
  },
  {
    type: "route",
    id: "form-builder",
    name: "Form Builder",
    path: "/form_builder",
    component: "FormsDataGrid",
    permission: view(PERMISSION_MODULES.SETTINGS),
  },
  {
    type: "route",
    id: "deployment-form-mapping",
    name: "Deployment Form Mapping",
    path: "/deployment-form-mapping",
    component: "DeploymentFormMapping",
    permission: edit(PERMISSION_MODULES.SETTINGS),
  },
  {
    type: "route",
    id: "form-builder-action",
    name: "Form Builder Action",
    path: "/form/:action",
    component: "FormBuilderWrapper",
    permission: edit(PERMISSION_MODULES.SETTINGS),
  },
  {
    type: "route",
    id: "analytics",
    name: "Analytics",
    path: "/analytics",
    component: "ReportsAnalytics",
    permission: view(PERMISSION_MODULES.REPORTS),
  },
  {
    type: "route",
    id: "not-found",
    name: "Page Not Found",
    path: "*",
    component: "PageNotFound",
  },
];

/** @type {RouteNode} */
const privateRoutes = {
  type: "group",
  id: "private",
  guard: "private",
  children: [
    {
      type: "layout",
      id: "admin-layout",
      layout: "subheader",
      children: adminSubheaderRoutes,
    },
    {
      type: "route",
      id: "questions",
      name: "Mentor Questions",
      path: "/questions",
      component: "MentorQuestions",
    },
    {
      type: "route",
      id: "login-type",
      name: "Login Type",
      path: "/login-type",
      component: "LoginType",
    },
    {
      type: "layout",
      id: "main-layout",
      layout: "default",
      children: mainAppRoutes,
    },
    {
      type: "route",
      id: "individual",
      name: "Individual Page",
      path: "/individual",
      component: "IndividualPage",
    },
    {
      type: "route",
      id: "form-link",
      name: "Form Link",
      path: "/form_link/:paramsId",
      component: "FormLink",
    },
  ],
};

export default privateRoutes;
